// The bash script a remote worker deploy pipes into `ssh`.
//
// Pure string generation: no spawning, no filesystem, no HTTP. That keeps the
// thing most likely to be wrong — several hundred lines of shell — assertable in
// a unit test instead of only observable against a real box.
//
// Two invariants the script has to hold, both of them security properties:
//
//   Never `set -x`. VEIL_KEY and AGENT_TOKEN reach the box inside the .env
//   heredoc, and operation logs are readable by any operator. exec.ts redacts
//   output as a second line of defence, but tracing would dump the key on a line
//   the redactor has no reason to recognise.
//
//   Idempotent throughout. A rerun of the same deploy is how an operator retries
//   a half-failed one, so every step is either a no-op or converges: files are
//   rewritten with identical content, `compose up -d` reconciles, firewall rules
//   are checked before they are added.

import { z } from "zod";
import { WorkerNameSchema } from "../schemas/provision.ts";
import {
	COMPOSE_PROJECT_NAME,
	renderRemoteWorkerCompose,
	renderRemoteWorkerEnvFile,
	WORKER_PORT,
} from "./remote-compose.ts";

/** Parent of the per-worker directory on the remote host. */
export const REMOTE_WORKER_ROOT = "/opt/veil-worker";

/** Compose file name on the remote host. */
export const REMOTE_COMPOSE_FILE_NAME = "compose.yaml";

/**
 * Last line of a successful run. The manager treats its absence as failure even
 * on exit 0: a script killed mid-stream by a dropped connection can still leave
 * ssh reporting success.
 */
export const BOOTSTRAP_SENTINEL = "VEILDONE";

/** Prefix of the step markers the script emits so a timeline can follow along. */
export const STEP_MARKER = "VEILSTEP";

/** Prefix of the `key=value` facts the check script reports. */
export const CHECK_MARKER = "VEILCHECK";

/** Exit codes the script uses, so a failure is diagnosable from the code alone. */
export const BOOTSTRAP_EXIT = {
	unsupportedPlatform: 78,
	noPrivileges: 77,
	dockerMissing: 76,
	unhealthy: 75,
} as const;

/** Per-worker directory on the remote host. */
export function remoteWorkerDir(hostname: string): string {
	return `${REMOTE_WORKER_ROOT}/${hostname}`;
}

export const RemoteBootstrapOptionsSchema = z.object({
	hostname: WorkerNameSchema,
	/** Rendered compose file — see renderRemoteWorkerCompose. */
	compose: z.string().min(1),
	/** Rendered .env holding VEIL_KEY (and AGENT_TOKEN when the agent is on). */
	envFile: z.string().min(1),
	/**
	 * Install Docker with get.docker.com when it is absent. Off means "refuse
	 * rather than pipe a remote script into root's shell without being asked".
	 */
	installDocker: z.boolean().default(false),
	/**
	 * Restrict inbound access to the worker port to this address. The manager's
	 * own egress IP, normally. Null leaves the host firewall alone.
	 */
	firewallAllowIp: z.string().trim().min(1).nullable().default(null),
	/**
	 * Registry the host must authenticate to before it can pull. Null for the
	 * license gate, which authenticates upstream itself off the org key in the
	 * image path and wants no client credential.
	 */
	registryLogin: z
		.object({
			registry: z.string().trim().min(1),
			username: z.string().trim().min(1),
			password: z.string().min(1),
		})
		.nullable()
		.default(null),
	/** Seconds to wait for the worker's own /health on the remote loopback. */
	healthTimeoutSec: z.coerce.number().int().positive().default(300),
	workerPort: z.coerce.number().int().min(1).max(65535).default(WORKER_PORT),
	workerDir: z.string().trim().min(1).optional(),
	composeFileName: z.string().trim().min(1).default(REMOTE_COMPOSE_FILE_NAME),
});
export type RemoteBootstrapOptions = z.input<
	typeof RemoteBootstrapOptionsSchema
>;

/**
 * `command <<'EOF' … EOF`, quoted so the body reaches the remote host verbatim
 * with no expansion.
 *
 * A body containing its own delimiter would end the heredoc early and spill the
 * rest of the file into the shell as commands. The delimiters are fixed and the
 * bodies are generated, so this cannot happen in practice — which is exactly why
 * it should be a hard failure rather than a silent one if it ever does.
 */
function heredocCommand(
	command: string,
	delimiter: string,
	body: string,
): string[] {
	if (body.split("\n").some((line) => line.trimEnd() === delimiter)) {
		throw new Error(
			`refusing to build the bootstrap script: content contains the heredoc delimiter "${delimiter}"`,
		);
	}
	return [`${command} <<'${delimiter}'`, body.replace(/\n$/, ""), delimiter];
}

/**
 * `set`, `umask` and the say/step/die helpers. Shared so the two generated
 * scripts cannot drift on the invariants at the top of this file.
 */
function scriptHeaderLines(title: string): string[] {
	return [
		`# ${title}`,
		"# Shell tracing is deliberately never enabled: this script carries secrets.",
		"set -euo pipefail",
		// Anything these scripts create holds a secret or sits next to one.
		"umask 077",
		"",
		"say()  { printf '%s\\n' \"$*\"; }",
		`step() { printf '${STEP_MARKER} %s\\n' "$1"; }`,
		'die()  { printf \'ERROR: %s\\n\' "$2" >&2; exit "$1"; }',
	];
}

/**
 * Architecture, a download tool, and privileges — the three things every later
 * stage assumes. Re-run by both scripts because each is a fresh shell and
 * `SUDO` / `FETCH` / `PROBE` do not survive one; all three checks are
 * read-only, so repeating them costs a few milliseconds.
 */
function platformLines(): string[] {
	return [
		"step platform",
		'ARCH="$(uname -m)"',
		'case "$ARCH" in',
		"  x86_64|amd64|aarch64|arm64) ;;",
		`  *) die ${BOOTSTRAP_EXIT.unsupportedPlatform} "unsupported architecture $ARCH — the worker image is built for x86_64 and arm64" ;;`,
		"esac",
		'say "arch: $ARCH"',
		"",
		"if command -v curl >/dev/null 2>&1; then",
		"  FETCH='curl -fsSL'",
		"  PROBE='curl -fsS -m 5 -o /dev/null'",
		"elif command -v wget >/dev/null 2>&1; then",
		"  FETCH='wget -qO-'",
		"  PROBE='wget -q -T 5 -O /dev/null'",
		"else",
		`  die ${BOOTSTRAP_EXIT.unsupportedPlatform} "neither curl nor wget is installed, so nothing here can download or probe"`,
		"fi",
		"",
		'if [ "$(id -u)" -eq 0 ]; then',
		"  SUDO=''",
		"elif command -v sudo >/dev/null 2>&1 && sudo -n true >/dev/null 2>&1; then",
		"  SUDO='sudo -n'",
		"else",
		`  die ${BOOTSTRAP_EXIT.noPrivileges} "this user is not root and has no passwordless sudo, so it cannot manage Docker"`,
		"fi",
	];
}

/**
 * Docker present-or-installed, plus the compose plugin.
 *
 * Emitted by both scripts. In the deploy script it is an assertion that costs
 * one `docker info`, and it is what makes that script safe to run on its own —
 * a redeploy of a host whose Docker has since been removed fails here with a
 * diagnosable exit rather than at `compose up`.
 */
function dockerLines(installDocker: boolean): string[] {
	const lines = [
		"step docker",
		"if command -v docker >/dev/null 2>&1 && $SUDO docker info >/dev/null 2>&1; then",
		'  say "docker present: $($SUDO docker --version)"',
		"else",
	];
	if (installDocker) {
		lines.push(
			// get.docker.com needs systemd to enable the daemon it installs, so
			// the check belongs here rather than in the platform step: a box that
			// already runs Docker does not need systemd for anything we do.
			"  if ! command -v systemctl >/dev/null 2>&1; then",
			`    die ${BOOTSTRAP_EXIT.unsupportedPlatform} "Docker is not installed and this host has no systemd, so the official install script cannot run. Install Docker by hand, then retry."`,
			"  fi",
			'  say "installing docker via get.docker.com"',
			'  INSTALL_SCRIPT="$(mktemp)"',
			'  $FETCH https://get.docker.com > "$INSTALL_SCRIPT"',
			'  $SUDO sh "$INSTALL_SCRIPT"',
			'  rm -f "$INSTALL_SCRIPT"',
			"  $SUDO systemctl enable --now docker",
			'  say "docker installed: $($SUDO docker --version)"',
		);
	} else {
		lines.push(
			`  die ${BOOTSTRAP_EXIT.dockerMissing} "Docker is not installed (or this user cannot reach its socket) and automatic installation was not enabled for this deploy"`,
		);
	}
	lines.push(
		"fi",
		"",
		"if ! $SUDO docker compose version >/dev/null 2>&1; then",
		`  die ${BOOTSTRAP_EXIT.dockerMissing} "the docker compose plugin is missing. Install docker-compose-plugin, then retry."`,
		"fi",
	);
	return lines;
}

/**
 * Everything a host needs before a worker can be written onto it, and nothing
 * that touches the worker itself.
 *
 * Its own ssh call so the manager can register the worker and put its address
 * on the release origin's allowlist BEFORE the container starts. The container's
 * own first act is to fetch manifest.json; run in one pass, that fetch happened
 * three steps before the render that would have allowed it, so it always 403'd
 * and the worker came up with no browser installed.
 *
 * Read-only unless `installDocker` is set, and idempotent either way.
 */
export function buildRemotePrepareScript(
	options: { installDocker?: boolean } = {},
): string {
	const lines = [
		...scriptHeaderLines(
			"VeilBrowser remote host preparation. Generated by cluster-manager.",
		),
		"",
		'say "veil worker preparation on $(uname -n 2>/dev/null || echo host)"',
		"",
		...platformLines(),
		"",
		...dockerLines(options.installDocker === true),
		"",
		"step done",
		`say ${shellQuote(BOOTSTRAP_SENTINEL)}`,
		"",
	];
	return `${lines.join("\n")}\n`;
}

/**
 * Take a worker's stack off its host and remove its directory.
 *
 * `set -uo pipefail` without `-e`, unlike every other script here: this one runs
 * while the FleetHost row is already gone, so there is nothing left to converge
 * on and every stage is best-effort. An absent compose file is a success — it is
 * what a second uninstall looks like — and a `compose down` that fails should
 * still let the directory removal be attempted and reported.
 *
 * The install volume is kept unless asked for, matching the local teardown: it
 * holds the multi-GB chromium build, the profile bundles and sessions.db, and
 * deleting those because someone removed a worker record would be a surprise.
 */
export function buildRemoteUninstallScript(options: {
	hostname: string;
	removeVolume?: boolean;
	workerDir?: string;
	composeFileName?: string;
}): string {
	const hostname = WorkerNameSchema.parse(options.hostname);
	const dir = options.workerDir ?? remoteWorkerDir(hostname);
	const composeFile = options.composeFileName ?? REMOTE_COMPOSE_FILE_NAME;
	const down = options.removeVolume
		? "down --remove-orphans --volumes"
		: "down --remove-orphans";

	const lines = [
		...scriptHeaderLines(
			"VeilBrowser remote worker uninstall. Generated by cluster-manager.",
		).map((line) => (line === "set -euo pipefail" ? "set -uo pipefail" : line)),
		"",
		`WORKER_DIR=${shellQuote(dir)}`,
		`COMPOSE_FILE="$WORKER_DIR/${composeFile}"`,
		`COMPOSE_PROJECT=${shellQuote(COMPOSE_PROJECT_NAME)}`,
		"",
		'if [ "$(id -u)" -eq 0 ]; then',
		"  SUDO=''",
		"elif command -v sudo >/dev/null 2>&1 && sudo -n true >/dev/null 2>&1; then",
		"  SUDO='sudo -n'",
		"else",
		`  die ${BOOTSTRAP_EXIT.noPrivileges} "this user is not root and has no passwordless sudo, so it cannot stop the worker"`,
		"fi",
		"",
		"step deploy",
		'if [ -f "$COMPOSE_FILE" ]; then',
		`  if $SUDO docker compose -p "$COMPOSE_PROJECT" -f "$COMPOSE_FILE" ${down}; then`,
		'    say "stopped and removed the worker stack in $WORKER_DIR"',
		"  else",
		'    say "WARN: docker compose down failed in $WORKER_DIR; its containers may still be running"',
		"  fi",
		"else",
		'  say "no compose file at $COMPOSE_FILE, so there was no stack to stop"',
		"fi",
		"",
		'if $SUDO rm -rf "$WORKER_DIR"; then',
		'  say "removed $WORKER_DIR"',
		"else",
		'  say "WARN: could not remove $WORKER_DIR; it has to be deleted by hand"',
		"fi",
		"",
		"step done",
		`say ${shellQuote(BOOTSTRAP_SENTINEL)}`,
		"",
	];
	return `${lines.join("\n")}\n`;
}

/**
 * Build the idempotent bootstrap script for one remote worker.
 *
 * Order matters: platform, then privileges, then Docker, then files, then the
 * stack, then health, then the firewall. The firewall goes last on purpose — a
 * rule that narrows access to the manager, applied before the worker is known
 * healthy, would lock out the very connection used to diagnose it.
 */
export function buildRemoteBootstrapScript(
	options: RemoteBootstrapOptions,
): string {
	const opts = RemoteBootstrapOptionsSchema.parse(options);
	const dir = opts.workerDir ?? remoteWorkerDir(opts.hostname);

	const lines: string[] = [
		...scriptHeaderLines(
			"VeilBrowser remote worker bootstrap. Generated by cluster-manager.",
		),
		"",
		`WORKER_NAME=${shellQuote(opts.hostname)}`,
		`WORKER_DIR=${shellQuote(dir)}`,
		`COMPOSE_FILE="$WORKER_DIR/${opts.composeFileName}"`,
		'ENV_FILE="$WORKER_DIR/.env"',
		`COMPOSE_PROJECT=${shellQuote(COMPOSE_PROJECT_NAME)}`,
		`WORKER_PORT=${opts.workerPort}`,
		`HEALTH_TIMEOUT=${opts.healthTimeoutSec}`,
		"",
		'say "veil worker bootstrap: $WORKER_NAME -> $WORKER_DIR"',
		"",
		...platformLines(),
		"",
		...dockerLines(opts.installDocker),
		"",
	];

	if (opts.registryLogin) {
		lines.push(...registryLoginLines(opts.registryLogin), "");
	}

	lines.push(
		"step deploy",
		'$SUDO mkdir -p "$WORKER_DIR"',
		'$SUDO chmod 0700 "$WORKER_DIR"',
		"",
		// tee rather than a redirect: with sudo, the redirect is performed by the
		// unprivileged shell and would fail on a root-owned directory.
		...heredocCommand(
			'$SUDO tee "$COMPOSE_FILE" >/dev/null',
			"VEIL_COMPOSE_EOF",
			opts.compose,
		),
		'$SUDO chmod 0600 "$COMPOSE_FILE"',
		"",
		...heredocCommand(
			'$SUDO tee "$ENV_FILE" >/dev/null',
			"VEIL_ENV_EOF",
			opts.envFile,
		),
		'$SUDO chmod 0600 "$ENV_FILE"',
		'say "wrote $COMPOSE_FILE and $ENV_FILE"',
		"",
		'cd "$WORKER_DIR"',
		'$SUDO docker compose -p "$COMPOSE_PROJECT" -f "$COMPOSE_FILE" pull',
		'$SUDO docker compose -p "$COMPOSE_PROJECT" -f "$COMPOSE_FILE" up -d --remove-orphans',
		"",
		"step health",
		'say "waiting up to ${HEALTH_TIMEOUT}s for the worker to answer on 127.0.0.1:${WORKER_PORT} (first boot downloads chromium + profile bundles)"',
		"DEADLINE=$(( $(date +%s) + HEALTH_TIMEOUT ))",
		'until $PROBE "http://127.0.0.1:${WORKER_PORT}/health" >/dev/null 2>&1; do',
		'  if [ "$(date +%s)" -ge "$DEADLINE" ]; then',
		'    say "--- last 60 lines of worker log ---"',
		'    $SUDO docker compose -p "$COMPOSE_PROJECT" -f "$COMPOSE_FILE" logs --tail 60 local-api 2>&1 || true',
		`    die ${BOOTSTRAP_EXIT.unhealthy} "the worker did not answer /health within \${HEALTH_TIMEOUT}s"`,
		"  fi",
		"  sleep 3",
		"done",
		'say "worker healthy on 127.0.0.1:${WORKER_PORT}"',
	);

	if (opts.firewallAllowIp) {
		lines.push("", ...firewallLines(opts.firewallAllowIp));
	}

	lines.push("", "step done", `say ${shellQuote(BOOTSTRAP_SENTINEL)}`, "");

	return `${lines.join("\n")}\n`;
}

/**
 * Authenticate the host to a private image registry.
 *
 * `--password-stdin` with a heredoc rather than `--password`: an argument is
 * visible in `ps` to every user on the box for the life of the call, and this
 * script runs as root.
 *
 * The credential stays in root's docker config afterwards, deliberately. It has
 * to: the updater-agent pulls with the same daemon when the manager rolls this
 * worker later, and a `docker logout` here would make every future roll fail
 * with a 403. The manager says so in the log rather than leaving it implicit.
 */
function registryLoginLines(login: {
	registry: string;
	username: string;
	password: string;
}): string[] {
	return [
		`say "authenticating to ${login.registry} as ${login.username}"`,
		...heredocCommand(
			`$SUDO docker login ${shellQuote(login.registry)} --username ${shellQuote(
				login.username,
			)} --password-stdin`,
			"VEIL_REGISTRY_PW_EOF",
			login.password,
		),
		`say "NOTE: those registry credentials now live in root's docker config on this host, because the updater-agent needs them to roll this worker later."`,
	];
}

/**
 * Narrow inbound access to the worker port to one address.
 *
 * ufw first when it is active, because adding iptables rules underneath an
 * active ufw is how operators end up with a firewall whose visible state does
 * not describe its behaviour.
 *
 * Both paths have to get ordering right, not just presence: ufw and iptables
 * both stop at the first match, so a blanket deny sitting above the allow makes
 * the worker unreachable from the manager while looking correctly configured.
 * The ufw path drops and re-adds the deny so it always lands last.
 *
 * Deliberately not persisted: writing iptables-save output would be a second,
 * distro-specific mechanism to get wrong, and the deploy has no way to verify it
 * survived. The manager reports that the rule is runtime-only.
 */
function firewallLines(allowIp: string): string[] {
	const ip = shellQuote(allowIp);
	return [
		"step firewall",
		`ALLOW_IP=${ip}`,
		'if command -v ufw >/dev/null 2>&1 && $SUDO ufw status 2>/dev/null | grep -qi "^Status: active"; then',
		// Drop any blanket deny first so the allow cannot end up underneath it.
		'  $SUDO ufw --force delete deny "${WORKER_PORT}/tcp" >/dev/null 2>&1 || true',
		'  if $SUDO ufw status | grep -q "${WORKER_PORT}/tcp.*ALLOW.*${ALLOW_IP}"; then',
		'    say "ufw already allows ${ALLOW_IP} to ${WORKER_PORT}/tcp"',
		"  else",
		'    $SUDO ufw allow from "$ALLOW_IP" to any port "$WORKER_PORT" proto tcp',
		'    say "ufw: allowed ${ALLOW_IP} to ${WORKER_PORT}/tcp"',
		"  fi",
		'  $SUDO ufw deny "${WORKER_PORT}/tcp"',
		'  say "ufw: ${WORKER_PORT}/tcp denied from everywhere else"',
		"elif command -v iptables >/dev/null 2>&1; then",
		// DOCKER-USER is consulted before docker's own published-port DNAT
		// accepts, so a rule in the filter INPUT chain would never be reached for
		// a container port. This is the chain docker documents for exactly this.
		"  if ! $SUDO iptables -L DOCKER-USER >/dev/null 2>&1; then",
		'    say "WARN: no DOCKER-USER chain, so the firewall step was skipped. Restrict ${WORKER_PORT}/tcp by hand."',
		"  else",
		'    if $SUDO iptables -C DOCKER-USER -p tcp --dport "$WORKER_PORT" -s "$ALLOW_IP" -j ACCEPT 2>/dev/null; then',
		'      say "iptables already allows ${ALLOW_IP} to ${WORKER_PORT}/tcp"',
		"    else",
		'      $SUDO iptables -I DOCKER-USER 1 -p tcp --dport "$WORKER_PORT" -s "$ALLOW_IP" -j ACCEPT',
		'      say "iptables: allowed ${ALLOW_IP} to ${WORKER_PORT}/tcp"',
		"    fi",
		'    if $SUDO iptables -C DOCKER-USER -p tcp --dport "$WORKER_PORT" -j DROP 2>/dev/null; then',
		'      say "iptables already drops ${WORKER_PORT}/tcp from everywhere else"',
		"    else",
		'      $SUDO iptables -A DOCKER-USER -p tcp --dport "$WORKER_PORT" -j DROP',
		'      say "iptables: dropped ${WORKER_PORT}/tcp from everywhere else"',
		"    fi",
		'    say "NOTE: iptables rules are runtime-only and will not survive a reboot on a host with no rule persistence."',
		"  fi",
		"else",
		'  say "WARN: neither ufw nor iptables is available, so the firewall step was skipped. The worker port is open to the internet."',
		"fi",
	];
}

/** POSIX single-quoting, so a generated value can never become shell syntax. */
export function shellQuote(value: string): string {
	return `'${value.replace(/'/g, `'\\''`)}'`;
}

export const RemoteCheckOptionsSchema = z.object({
	workerPort: z.coerce.number().int().min(1).max(65535).default(WORKER_PORT),
	hostname: WorkerNameSchema.optional(),
});
export type RemoteCheckOptions = z.input<typeof RemoteCheckOptionsSchema>;

/**
 * A read-only probe of a candidate host: what it runs, whether Docker is there,
 * whether the worker port is already taken, whether this user can act.
 *
 * Exists so the wizard can fail fast. Everything it reports is a precondition
 * whose absence turns a deploy into a several-minute walk to an error.
 */
export function buildRemoteCheckScript(
	options: RemoteCheckOptions = {},
): string {
	const opts = RemoteCheckOptionsSchema.parse(options);
	const dir = opts.hostname ? remoteWorkerDir(opts.hostname) : null;

	const lines = [
		"# VeilBrowser remote host check. Read-only.",
		"set -uo pipefail",
		`WORKER_PORT=${opts.workerPort}`,
		`fact() { printf '${CHECK_MARKER} %s=%s\\n' "$1" "$2"; }`,
		"",
		'fact kernel "$(uname -sr 2>/dev/null || echo unknown)"',
		'fact arch "$(uname -m 2>/dev/null || echo unknown)"',
		"if [ -r /etc/os-release ]; then",
		"  . /etc/os-release",
		'  fact os "${PRETTY_NAME:-${NAME:-unknown}}"',
		"else",
		"  fact os unknown",
		"fi",
		'if [ "$(id -u)" -eq 0 ]; then',
		"  fact root yes; fact sudo yes",
		"else",
		"  fact root no",
		"  if command -v sudo >/dev/null 2>&1 && sudo -n true >/dev/null 2>&1; then",
		"    fact sudo yes",
		"  else",
		"    fact sudo no",
		"  fi",
		"fi",
		"if command -v systemctl >/dev/null 2>&1; then fact systemd yes; else fact systemd no; fi",
		"if command -v docker >/dev/null 2>&1; then",
		"  fact docker yes",
		'  fact dockerVersion "$(docker --version 2>/dev/null | head -n1)"',
		"  if docker info >/dev/null 2>&1 || sudo -n docker info >/dev/null 2>&1; then",
		"    fact dockerUsable yes",
		"  else",
		"    fact dockerUsable no",
		"  fi",
		"  if docker compose version >/dev/null 2>&1 || sudo -n docker compose version >/dev/null 2>&1; then",
		"    fact dockerCompose yes",
		"  else",
		"    fact dockerCompose no",
		"  fi",
		"else",
		"  fact docker no; fact dockerUsable no; fact dockerCompose no",
		"fi",
		// ss, then netstat, then a loopback connect: minimal images ship none of
		// the first two, and "cannot tell" must not read as "port is free".
		"if command -v ss >/dev/null 2>&1; then",
		'  if ss -lnt 2>/dev/null | grep -q ":${WORKER_PORT} "; then fact portBound yes; else fact portBound no; fi',
		"elif command -v netstat >/dev/null 2>&1; then",
		'  if netstat -lnt 2>/dev/null | grep -q ":${WORKER_PORT} "; then fact portBound yes; else fact portBound no; fi',
		"else",
		"  fact portBound unknown",
		"fi",
	];

	if (dir) {
		lines.push(
			`if [ -d ${shellQuote(dir)} ]; then fact existingDir yes; else fact existingDir no; fi`,
		);
	}

	lines.push("", `printf '${CHECK_MARKER} ok=yes\\n'`, "");
	return lines.join("\n");
}

export interface RemoteCheckReport {
	os: string | null;
	kernel: string | null;
	arch: string | null;
	hasSystemd: boolean;
	isRoot: boolean;
	hasSudo: boolean;
	hasDocker: boolean;
	dockerUsable: boolean;
	hasCompose: boolean;
	dockerVersion: string | null;
	/** null when the host shipped no way to look. */
	workerPortBound: boolean | null;
	existingWorkerDir: boolean;
	/** False when the script did not run to completion. */
	complete: boolean;
}

/**
 * Parse the check script's output. Tolerant by design: a truncated run still
 * reports whatever facts arrived, with `complete: false` telling the caller not
 * to trust an absent one as a "no".
 */
export function parseRemoteCheckOutput(text: string): RemoteCheckReport {
	const facts = new Map<string, string>();
	const prefix = `${CHECK_MARKER} `;
	for (const line of text.split("\n")) {
		const trimmed = line.trim();
		if (!trimmed.startsWith(prefix)) continue;
		const body = trimmed.slice(prefix.length);
		const cut = body.indexOf("=");
		if (cut <= 0) continue;
		facts.set(body.slice(0, cut), body.slice(cut + 1).trim());
	}

	const str = (key: string): string | null => {
		const value = facts.get(key);
		return value && value !== "unknown" ? value : null;
	};
	const bool = (key: string): boolean => facts.get(key) === "yes";

	const portBound = facts.get("portBound");
	return {
		os: str("os"),
		kernel: str("kernel"),
		arch: str("arch"),
		hasSystemd: bool("systemd"),
		isRoot: bool("root"),
		hasSudo: bool("sudo"),
		hasDocker: bool("docker"),
		dockerUsable: bool("dockerUsable"),
		hasCompose: bool("dockerCompose"),
		dockerVersion: str("dockerVersion"),
		workerPortBound:
			portBound === "yes" ? true : portBound === "no" ? false : null,
		existingWorkerDir: bool("existingDir"),
		complete: facts.get("ok") === "yes",
	};
}

/** True when the bootstrap script ran all the way to its sentinel. */
export function bootstrapSucceeded(output: string): boolean {
	return output.split("\n").some((line) => line.trim() === BOOTSTRAP_SENTINEL);
}

/** Step names the script marks, in the order it emits them. */
export function parseStepMarkers(output: string): string[] {
	const prefix = `${STEP_MARKER} `;
	const steps: string[] = [];
	for (const line of output.split("\n")) {
		const trimmed = line.trim();
		if (trimmed.startsWith(prefix)) steps.push(trimmed.slice(prefix.length));
	}
	return steps;
}

/**
 * Render compose + .env + script in one call, so a caller cannot render the
 * compose file with placeholder secrets and then ship it over SSH — the two
 * modes exist for different transports and mixing them writes a broken file.
 */
export function buildRemoteDeployPayload(args: {
	hostname: string;
	orgKey: string;
	compose: Parameters<typeof renderRemoteWorkerCompose>[0];
	agentToken: string | null;
	installDocker?: boolean;
	firewallAllowIp?: string | null;
	healthTimeoutSec?: number;
	registryLogin?: RemoteBootstrapOptions["registryLogin"];
}): { compose: string; envFile: string; script: string } {
	const compose = renderRemoteWorkerCompose({
		...args.compose,
		secretMode: "env-file",
		composeFileName: REMOTE_COMPOSE_FILE_NAME,
	});
	const envFile = renderRemoteWorkerEnvFile({
		orgKey: args.orgKey,
		agentToken: args.agentToken,
	});
	const script = buildRemoteBootstrapScript({
		hostname: args.hostname,
		compose,
		envFile,
		installDocker: args.installDocker,
		firewallAllowIp: args.firewallAllowIp ?? null,
		healthTimeoutSec: args.healthTimeoutSec,
		registryLogin: args.registryLogin ?? null,
	});
	return { compose, envFile, script };
}
